import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DataTable from "react-data-table-component";
import axios, { AxiosError } from "axios";
import { useMsal } from "@azure/msal-react";
import { getApiBaseUrl, getAppBaseUrl } from "./utils/baseUrl";
import { useLoading, acquireTokenSafe } from "./App";

const apiUrl = getApiBaseUrl();

// Define the type for changelog entries
interface ChangelogEntry {
  date: string;
  version: string;
  description: string;
}

// Define the type for cache entries
interface CacheEntry {
  name: string;
  lastCached: string;
}

// Sample changelog data
const changelogData: ChangelogEntry[] = [
  {
    date: "2025-03-28",
    version: "0.1.0",
    description:
      "Initial Beta release. Included fundamental reporting from Senta for Clients, Delivery, and Partners.",
  },
];

// Changelog DataTable columns
const changelogColumns = [
  {
    name: "Date",
    selector: (row: ChangelogEntry) => row.date,
    sortable: true,
    width: "120px",
  },
  {
    name: "Version",
    selector: (row: ChangelogEntry) => row.version,
    sortable: true,
    width: "100px",
  },
  {
    name: "Description",
    selector: (row: ChangelogEntry) => row.description,
    grow: 2,
  },
];



const CachingPage = () => {
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading(); // Use the global loading context
  const [cacheRecords, setCacheRecords] = useState<CacheEntry[]>([]);
  const [cachingEnabled, setCachingEnabled] = useState(true);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  const getFileAge = (date: string) => {
    const now = new Date();
    const fileDate = new Date(date);
    const diffMs = now.getTime() - fileDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  };

  const downloadCacheFile = async (fileName: string) => {
    try {
      const accounts = instance.getAllAccounts();
      if (!accounts.length) return;

      const token = await acquireTokenSafe(instance, accounts);
      if (!token) return;
      const authHeader = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/api/cache/download/${fileName}`, {
        headers: {
          'Authorization': authHeader,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Helper to get MSAL token
  const getToken = async () => {
    const token = await acquireTokenSafe(instance, accounts);
    if (!token) throw new Error("No account found, please log in");
    return token;
  };

  // Fetch cache records and initial caching state from server
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Start the loading bar
      try {
        const token = await getToken();
        const [listResponse, stateResponse] = await Promise.all([
          axios.get(`${apiUrl}/api/cache/list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiUrl}/api/cache/state`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        console.log("Cache records:", listResponse.data);
        console.log("Caching state:", stateResponse.data);

        setCacheRecords(listResponse.data);
        setCachingEnabled(stateResponse.data.isCachingEnabled);
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error(
          "Error fetching data:",
          axiosError.response?.data || axiosError.message,
        );
      } finally {
        setLoading(false); // Stop the loading bar
      }
    };
    fetchData();
  }, [instance, accounts]);

  // Clear all cache
  const handleClearAll = async () => {
    try {
      const token = await getToken();
      await axios.post(
        `${apiUrl}/api/cache/clear`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCacheRecords([]);
      console.log("All cache cleared");
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(
        "Error clearing all cache:",
        axiosError.response?.data || axiosError.message,
      );
    }
  };

  // Clear specific cache item
  const handleClearItem = async (blobName: string) => {
    try {
      const token = await getToken();
      await axios.post(
        `${apiUrl}/api/cache/clear/${encodeURIComponent(blobName)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCacheRecords(
        cacheRecords.filter((record) => record.name !== blobName),
      );
      console.log(`Cleared cache item: ${blobName}`);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(
        `Error clearing cache item ${blobName}:`,
        axiosError.response?.data || axiosError.message,
      );
    }
  };

  // Toggle caching
  const handleToggleCaching = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      const newState = event.target.checked;
      const token = await getToken();
      const response = await axios.post(
        `${apiUrl}/api/cache/toggle`,
        { enabled: newState },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCachingEnabled(newState);
      console.log(
        `Caching ${newState ? "enabled" : "disabled"}`,
        response.data,
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(
        "Error toggling caching:",
        axiosError.response?.data || axiosError.message,
      );
    }
  };

  // Cache DataTable columns
  const cacheColumns = [
    {
      name: "Cache Key",
      selector: (row: CacheEntry) => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Last Cached",
      selector: (row: CacheEntry) => new Date(row.lastCached).toLocaleString(),
      sortable: true,
    },
    {
      name: "Age",
      selector: (row: CacheEntry) => getFileAge(row.lastCached),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: CacheEntry) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => downloadCacheFile(row.name)}
            sx={{ 
              borderRadius: "6px", 
              textTransform: "none",
              minWidth: "80px",
              height: "28px",
              fontSize: "0.75rem",
              padding: "4px 8px"
            }}
          >
            Download
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleClearItem(row.name)}
            sx={{ 
              borderRadius: "6px",
              textTransform: "none",
              minWidth: "60px",
              height: "28px",
              fontSize: "0.75rem",
              padding: "4px 8px"
            }}
          >
            Clear
          </Button>
        </Box>
      ),
      ignoreRowClick: true,
      width: "180px",
      wrap: false,
    },
  ];

  return (
    <div
      style={{ padding: 20, backgroundColor: "#f5f7fa", minHeight: "100vh" }}
    >
      <Typography
        variant="h1"
        gutterBottom
        sx={{ fontWeight: 600, color: "#1a202c", mb: 4 }}
      >
        Metadata
      </Typography>
      <Grid container spacing={3}>
        {/* Right Column: Cache Records */}
        <Grid size={12}>
          <Paper
            elevation={3}
            sx={{ p: 3, borderRadius: "12px", bgcolor: "#fff" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h2"
                sx={{ color: "#2d3748", fontWeight: 500, fontSize: "1.5rem" }}
              >
                Cache Records
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cachingEnabled}
                      onChange={handleToggleCaching}
                      color="primary"
                    />
                  }
                  label="Enable Caching"
                />
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleClearAll}
                  sx={{ borderRadius: "8px", padding: "6px 16px" }}
                >
                  Clear All Cache
                </Button>
              </Box>
            </Box>
            <DataTable
              columns={cacheColumns}
              data={cacheRecords}
              pagination
              responsive
              customStyles={{
                table: {
                  style: {
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  },
                },
                headCells: {
                  style: {
                    backgroundColor: "#edf2f7",
                    fontWeight: 600,
                    color: "#2d3748",
                    fontSize: "1rem",
                  },
                },
                cells: {
                  style: {
                    padding: "12px",
                    color: "#4a5568",
                    fontSize: "0.95rem",
                  },
                },
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default CachingPage;